import { useState, FormEvent, useEffect, useCallback } from "react";

type BaseFormValues = {
  [key: string]: any;
};

type CastingFunction = (value: any) => any;

export const regexValidators = {
  email: new RegExp(/^\S+@\S+\.\S+$/),
  // https://www.oreilly.com/library/view/regular-expressions-cookbook/9781449327453/ch04s03.html#:~:text=Thanks%20to%20the%20international%20phone,in%20use%20contain%20seven%20digits.
  phone: new RegExp(/^\+(?:[0-9] ?){6,14}[0-9]$/),
  url: new RegExp(/^(http|https):\/\/[^ "]+$/, "i"),
};

export function useForm<T extends BaseFormValues>(initialValues: T) {
  const [formValues, setFormValues] = useState<T>(initialValues);

  useEffect(() => {
    setFormValues(initialValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(initialValues)]);

  const handleChange = (
    event: FormEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = event.currentTarget;
    const isCheckboxOrRadio = ["radio", "checkbox"].includes(type);

    if (isCheckboxOrRadio) {
      setFormValue(name, (event.currentTarget as HTMLInputElement).checked);
      return;
    }

    setFormValue(name, value);
  };

  const setFormValue = useCallback((attribute: string, value: any) => {
    setFormValues((formValues) => ({
      ...formValues,
      [attribute]: value,
    }));
  }, []);

  const renderArrayField = (
    name: string,
    renderFunction: Function,
    errors?: any,
    isDisabled = false
  ) => {
    const [focusedField, setFocusedField] = useState<string>();

    function addField(value: any = "") {
      setFormValues((formValues) => ({
        ...formValues,
        [name]: formValues[name].concat(value),
      }));
    }

    function removeField(index: number) {
      setFormValues((formValues) => ({
        ...formValues,
        [name]: (formValues[name] as any[]).filter(
          (_, filterIndex) => filterIndex !== index
        ),
      }));
    }

    function getValueByIndex(index: number) {
      return formValues[name][index];
    }

    function onFieldChange(index: number, value: any) {
      setFormValues((formValues) => ({
        ...formValues,
        [name]: (formValues[name] as any[]).map((currentValue, mapIndex) =>
          mapIndex === index ? value : currentValue
        ),
      }));
    }

    function onFocus(name: string) {
      setFocusedField(name);
    }

    return renderFunction({
      name,
      errors,
      onFocus,
      focusedField,
      addField,
      removeField,
      values: formValues[name],
      getValueByIndex,
      onFieldChange,
      isDisabled,
    });
  };

  return {
    formValues,
    setFormValues,
    setFormValue,
    handleChange,
    renderArrayField,
  };
}

export function getValue(value: any, casting?: CastingFunction): any {
  if (!value || value === "") return null;
  if (casting) return casting(value);
  return value;
}
